/**
 * Annotation state for one shared screen.
 *
 * Deliberately free of Solid: this is a plain object with a revision counter,
 * so the whole protocol can be exercised headlessly. The overlay component
 * wraps it and re-renders when the revision moves.
 *
 * One instance per screen-share publication (keyed by its SID). Two people
 * sharing at once means two independent overlays, and a presenter who stops
 * and starts again gets a new SID — that is what clears the ink when the
 * share is restarted, without any explicit "clear on restart" rule.
 *
 * ⭐ The presenter is the only authority, and only over policy. Nothing is
 * stored on a server; ink lives in each viewer's memory until it fades.
 */
import { assignInkColors, INK_PALETTE } from "./colors";
import { clampFrame } from "./geometry";
import {
  ANN_VERSION,
  chunkPoints,
  chunkSnapshot,
  MAX_LIVE_POINTS,
  ReceiveLimiter,
} from "./protocol";
import { SHAPE_FREE } from "./protocol";
import type { AnnMessage, SnapshotStroke, TrackKey } from "./protocol";
import { simplify, SIMPLIFY_EPSILON } from "./simplify";

/** Ink lifetime after the stroke ends. 0 keeps it until someone clears. */
export const DEFAULT_TTL_MS = 6000;

/** Last stretch of a stroke's life, spent fading out. */
export const FADE_MS = 1500;

/** Default pen width in frame units — about three pixels on a 1080p frame. */
export const DEFAULT_WIDTH = 12;

/** A cursor that has not moved for this long is assumed abandoned. */
export const CURSOR_IDLE_MS = 5000;

export interface Stroke {
  author: string;
  id: number;
  /** Flat [x0, y0, x1, y1, …] in frame units. */
  points: number[];
  color: string;
  /** Frame units, fraction of frame height. */
  width: number;
  ttl: number;
  /** 0 — от руки, иначе вид фигуры (SHAPE_* в protocol). */
  shape: number;
  done: boolean;
  endedAt?: number;
}

export interface Cursor {
  author: string;
  x: number;
  y: number;
  laser: boolean;
  at: number;
}

export interface Policy {
  draw: boolean;
  cursor: boolean;
  blocked: string[];
}

export type SendFn = (
  message: AnnMessage,
  options: { reliable: boolean; to?: string[] },
) => void;

export interface SessionOptions {
  track: TrackKey;
  /** Our own LiveKit identity. */
  self: string;
  /** Identity of whoever is sharing this screen. */
  presenter: string;
  send: SendFn;
  now?: () => number;
  onChange?: () => void;
  /** Overridable so tests do not have to fight a real clock. */
  limiter?: ReceiveLimiter;
  onLimit?: (identity: string) => void;
}

interface ActiveStroke {
  id: number;
  chunk: number;
  pending: number[];
  tail?: [number, number];
}

export class AnnotationSession {
  readonly track: TrackKey;
  readonly self: string;
  readonly presenter: string;

  #send: SendFn;
  #now: () => number;
  #onChange?: () => void;
  #onLimit?: (identity: string) => void;
  #limiter: ReceiveLimiter;

  #policy: Policy = { draw: true, cursor: true, blocked: [] };
  #strokes: Stroke[] = [];
  #cursors = new Map<string, Cursor>();
  #colors = new Map<string, string>();
  #revision = 0;

  #nextId = 1;
  /** Счётчик отправленных позиций указателя — см. moveCursor(). */
  #cursorPackets = 0;
  #active?: ActiveStroke;
  /** Style of the stroke in progress; travels in its opening chunk only. */
  #pendingStyle?: {
    width: number;
    ttl: number;
    colorIndex?: number;
    shape: number;
  };

  constructor(options: SessionOptions) {
    this.track = options.track;
    this.self = options.self;
    this.presenter = options.presenter;
    this.#send = options.send;
    this.#now = options.now ?? (() => Date.now());
    this.#onChange = options.onChange;
    this.#onLimit = options.onLimit;
    this.#limiter = options.limiter ?? new ReceiveLimiter({ now: this.#now });
    this.setRoster([options.self, options.presenter]);
  }

  get isPresenter(): boolean {
    return this.self === this.presenter;
  }

  revision(): number {
    return this.#revision;
  }

  policy(): Policy {
    return this.#policy;
  }

  strokes(): readonly Stroke[] {
    return this.#strokes;
  }

  cursors(): Cursor[] {
    return [...this.#cursors.values()];
  }

  colorOf(identity: string): string {
    return this.#colors.get(identity) ?? INK_PALETTE[0];
  }

  /** Recompute colours whenever the room's roster changes. */
  setRoster(identities: string[]): void {
    this.#colors = assignInkColors([
      ...identities,
      this.self,
      this.presenter,
    ]);
    this.#touch();
  }

  participantLeft(identity: string): void {
    this.#limiter.forget(identity);
    if (this.#cursors.delete(identity)) this.#touch();
  }

  // ---------------------------------------------------------------- incoming

  receive(from: string, message: AnnMessage): void {
    if (message.t !== this.track) return;
    // Our own packets never come back from the SFU; if one does, it is a loop.
    if (from === this.self) return;

    const points = message.k === "pts" ? message.p.length / 2 : 0;
    const verdict = this.#limiter.accept(from, points);
    if (verdict !== "ok") {
      if (verdict === "dropped-first") this.#onLimit?.(from);
      return;
    }

    switch (message.k) {
      case "hello":
        // Only the presenter answers, and only to the asker: this is how a
        // late joiner learns whether drawing is allowed and what is already
        // on the board.
        if (this.isPresenter) {
          this.#sendPolicy([from]);
          this.#sendSnapshot([from]);
        }
        return;

      case "policy":
        if (from !== this.presenter) return;
        this.#policy = {
          draw: message.draw,
          cursor: message.cursor,
          blocked: message.blocked,
        };
        this.#pruneBlocked();
        this.#touch();
        return;

      case "cur": {
        if (!this.#mayShowCursor(from)) return;
        this.#cursors.set(from, {
          author: from,
          x: message.x,
          y: message.y,
          laser: message.l === 1,
          at: this.#now(),
        });
        this.#touch();
        return;
      }

      case "out":
        if (this.#cursors.delete(from)) this.#touch();
        return;

      case "pts":
        if (!this.#mayDraw(from)) return;
        this.#append(from, message.id, message.n, message.p, {
          color: message.c,
          width: message.w,
          ttl: message.ttl,
          shape: message.s,
        });
        return;

      case "end": {
        const stroke = this.#find(from, message.id);
        if (!stroke) return;
        stroke.done = true;
        stroke.endedAt = this.#now();
        this.#touch();
        return;
      }

      case "undo": {
        const before = this.#strokes.length;
        this.#strokes = this.#strokes.filter(
          (s) => !(s.author === from && s.id === message.id),
        );
        if (this.#strokes.length !== before) this.#touch();
        return;
      }

      case "snap": {
        // The board's contents come from the presenter and nobody else —
        // otherwise any peer could inject ink attributed to a third person.
        if (from !== this.presenter) return;
        this.#applySnapshot(message.s);
        return;
      }

      case "clear": {
        // Anyone may wipe their own ink; wiping other people's is the
        // presenter's call alone.
        if (message.who) {
          if (message.who !== from && from !== this.presenter) return;
          this.#dropAuthor(message.who);
        } else {
          if (from !== this.presenter) return;
          this.#strokes = [];
          this.#active = undefined;
          this.#touch();
        }
        return;
      }
    }
  }

  // ---------------------------------------------------------------- outgoing

  /** Ask the presenter what is allowed. Sent when a share first appears. */
  hello(): void {
    if (this.isPresenter) return;
    this.#send(
      { k: "hello", t: this.track, v: ANN_VERSION },
      { reliable: true, to: [this.presenter] },
    );
  }

  setPolicy(patch: Partial<Policy>): void {
    if (!this.isPresenter) return;
    this.#policy = { ...this.#policy, ...patch };
    this.#pruneBlocked();
    this.#sendPolicy();
    this.#touch();
  }

  block(identity: string, blocked = true): void {
    if (!this.isPresenter) return;
    const set = new Set(this.#policy.blocked);
    if (blocked) set.add(identity);
    else set.delete(identity);
    this.setPolicy({ blocked: [...set] });
  }

  canDraw(): boolean {
    return this.#mayDraw(this.self);
  }

  beginStroke(options?: {
    width?: number;
    ttl?: number;
    colorIndex?: number;
    shape?: number;
  }): number | undefined {
    if (!this.canDraw()) return undefined;

    this.endStroke();

    const id = this.#nextId++;
    const width = options?.width ?? DEFAULT_WIDTH;
    const ttl = options?.ttl ?? DEFAULT_TTL_MS;
    const color =
      options?.colorIndex !== undefined
        ? INK_PALETTE[options.colorIndex % INK_PALETTE.length]
        : this.colorOf(this.self);

    const shape = options?.shape ?? SHAPE_FREE;

    this.#strokes.push({
      author: this.self,
      id,
      points: [],
      color,
      width,
      ttl,
      shape,
      done: false,
    });

    this.#active = { id, chunk: 0, pending: [] };
    this.#pendingStyle = { width, ttl, colorIndex: options?.colorIndex, shape };
    this.#touch();
    return id;
  }

  /**
   * Feed a point in frame units.
   *
   * Local ink keeps every sample so our own line is smooth immediately; the
   * thinned version goes on the wire at flush time.
   */
  addPoint(x: number, y: number): void {
    const active = this.#active;
    if (!active) return;

    const px = clampFrame(x);
    const py = clampFrame(y);

    const stroke = this.#find(this.self, active.id);

    // У фигуры всего две точки: начало и то, где сейчас указатель. Дописывать
    // бессмысленно — рамка тянется, а не рисуется.
    if (stroke && this.#pendingStyle?.shape) {
      if (stroke.points.length < 2) stroke.points.push(px, py);
      else stroke.points.splice(2, stroke.points.length, px, py);
      this.#touch();
      return;
    }

    stroke?.points.push(px, py);
    active.pending.push(px, py);
    this.#touch();
  }

  /** Push whatever has accumulated. Call at about 20 Hz while drawing. */
  flush(): void {
    const active = this.#active;
    if (!active) return;

    // Фигура уезжает целиком и всегда нулевым куском.
    if (this.#pendingStyle?.shape) {
      const stroke = this.#find(this.self, active.id);
      if (!stroke || stroke.points.length < 4) return;
      this.#send(
        {
          k: "pts",
          t: this.track,
          id: active.id,
          n: 0,
          p: stroke.points.slice(),
          w: this.#pendingStyle.width,
          ttl: this.#pendingStyle.ttl,
          s: this.#pendingStyle.shape,
          ...(this.#pendingStyle.colorIndex !== undefined
            ? { c: this.#pendingStyle.colorIndex }
            : {}),
        },
        { reliable: true },
      );
      return;
    }

    if (active.pending.length < 2) return;

    // Start the run at the last point already sent so the receiver has a
    // segment joining this batch to the previous one.
    const run = active.tail
      ? [active.tail[0], active.tail[1], ...active.pending]
      : active.pending.slice();

    const thinned = simplify(run, SIMPLIFY_EPSILON);
    active.pending = [];
    if (thinned.length < 2) return;
    active.tail = [thinned[thinned.length - 2], thinned[thinned.length - 1]];

    for (const chunk of chunkPoints(thinned)) {
      const n = active.chunk++;
      const message: AnnMessage =
        n === 0 && this.#pendingStyle
          ? {
              k: "pts",
              t: this.track,
              id: active.id,
              n,
              p: chunk,
              w: this.#pendingStyle.width,
              ttl: this.#pendingStyle.ttl,
              ...(this.#pendingStyle.colorIndex !== undefined
                ? { c: this.#pendingStyle.colorIndex }
                : {}),
            }
          : { k: "pts", t: this.track, id: active.id, n, p: chunk };

      this.#send(message, { reliable: true });
    }
  }

  endStroke(): void {
    const active = this.#active;
    if (!active) return;

    this.flush();
    this.#active = undefined;

    const stroke = this.#find(this.self, active.id);
    if (stroke) {
      // A stroke nobody actually drew (a click, not a drag) is noise.
      if (stroke.points.length < 4) {
        this.#strokes = this.#strokes.filter((s) => s !== stroke);
        this.#touch();
        return;
      }
      stroke.done = true;
      stroke.endedAt = this.#now();
    }

    this.#send({ k: "end", t: this.track, id: active.id }, { reliable: true });
    this.#touch();
  }

  undoLast(): void {
    for (let i = this.#strokes.length - 1; i >= 0; i--) {
      const stroke = this.#strokes[i];
      if (stroke.author !== this.self) continue;
      this.#strokes.splice(i, 1);
      this.#send(
        { k: "undo", t: this.track, id: stroke.id },
        { reliable: true },
      );
      this.#touch();
      return;
    }
  }

  clearMine(): void {
    this.#dropAuthor(this.self);
    this.#send(
      { k: "clear", t: this.track, who: this.self },
      { reliable: true },
    );
  }

  clearAll(): void {
    if (!this.isPresenter) return;
    this.#strokes = [];
    this.#active = undefined;
    this.#send({ k: "clear", t: this.track }, { reliable: true });
    this.#touch();
  }

  /**
   * Broadcast our pointer. Lossy on purpose: a dropped cursor sample is
   * replaced by the next one 50 ms later, and retransmitting stale positions
   * would only make the pointer lag.
   */
  moveCursor(x: number, y: number, laser = false): void {
    // The presenter's pointer is already in the captured picture; sending it
    // too would draw a second, differently delayed cursor next to the real one.
    if (this.isPresenter) return;

    const message: AnnMessage = laser
      ? { k: "cur", t: this.track, x: clampFrame(x), y: clampFrame(y), l: 1 }
      : { k: "cur", t: this.track, x: clampFrame(x), y: clampFrame(y) };

    // ⚠️ Каждый пятый пакет уходит надёжным каналом. Жалоба с прода: рисование
    // у показывающего видно, а курсора нет — а чернила как раз идут `reliable`.
    // Если у кого-то по дороге не проходит именно ненадёжный канал, указатель
    // пропадал целиком; так он в худшем случае обновляется 4 раза в секунду.
    // Все пакеты слать надёжно нельзя: потерянный пакет пересылался бы, и
    // указатель тянулся бы за мышью хвостом устаревших позиций.
    const reliable = this.#cursorPackets++ % 5 === 0;
    this.#send(message, { reliable });
  }

  cursorOut(): void {
    if (this.isPresenter) return;
    this.#send({ k: "out", t: this.track }, { reliable: true });
  }

  // ------------------------------------------------------------------ upkeep

  /** Drop faded ink and abandoned cursors. True when something went away. */
  sweep(): boolean {
    const now = this.#now();
    let changed = false;

    const kept = this.#strokes.filter((stroke) => {
      if (!stroke.done || stroke.ttl === 0) return true;
      return now - (stroke.endedAt ?? now) < stroke.ttl;
    });
    if (kept.length !== this.#strokes.length) {
      this.#strokes = kept;
      changed = true;
    }

    for (const [identity, cursor] of this.#cursors) {
      if (now - cursor.at > CURSOR_IDLE_MS) {
        this.#cursors.delete(identity);
        changed = true;
      }
    }

    if (changed) this.#touch();
    return changed;
  }

  /** 1 while alive, ramping to 0 over the stroke's last moments. */
  alphaOf(stroke: Stroke): number {
    if (!stroke.done || stroke.ttl === 0) return 1;
    const elapsed = this.#now() - (stroke.endedAt ?? 0);
    const left = stroke.ttl - elapsed;
    if (left <= 0) return 0;
    if (left >= FADE_MS) return 1;
    return left / FADE_MS;
  }

  /** True while anything on screen is still changing on its own. */
  get fading(): boolean {
    return this.#strokes.some((s) => s.done && s.ttl > 0);
  }

  // ------------------------------------------------------------------ private

  #sendPolicy(to?: string[]): void {
    this.#send(
      {
        k: "policy",
        t: this.track,
        draw: this.#policy.draw,
        cursor: this.#policy.cursor,
        blocked: this.#policy.blocked,
      },
      { reliable: true, to },
    );
  }

  /**
   * Permanent ink only.
   *
   * Vanishing strokes are gone in seconds; shipping them to a late joiner
   * would race their own expiry and show ink that everyone else has already
   * forgotten.
   */
  #sendSnapshot(to?: string[]): void {
    const board: SnapshotStroke[] = [];
    for (const stroke of this.#strokes) {
      if (!stroke.done || stroke.ttl !== 0) continue;
      if (stroke.points.length < 4) continue;
      board.push({
        a: stroke.author,
        id: stroke.id,
        p: stroke.points,
        w: stroke.width,
        ...(stroke.shape ? { s: stroke.shape } : {}),
      });
    }
    if (!board.length) return;

    const chunks = chunkSnapshot(board);
    chunks.forEach((s, i) =>
      this.#send(
        { k: "snap", t: this.track, i, n: chunks.length, s },
        { reliable: true, to },
      ),
    );
  }

  #applySnapshot(strokes: SnapshotStroke[]): void {
    let added = 0;
    for (const item of strokes) {
      // Blocked authors stay blocked even when their ink arrives second-hand.
      if (!this.#mayDraw(item.a)) continue;
      if (this.#find(item.a, item.id)) continue;

      this.#strokes.push({
        author: item.a,
        id: item.id,
        shape: item.s ?? SHAPE_FREE,
        points: item.p.slice(),
        color:
          item.c !== undefined
            ? INK_PALETTE[item.c % INK_PALETTE.length]
            : this.colorOf(item.a),
        width: item.w ?? DEFAULT_WIDTH,
        ttl: 0,
        done: true,
        endedAt: this.#now(),
      });
      added++;
    }
    if (added) this.#touch();
  }

  #mayDraw(identity: string): boolean {
    if (identity === this.presenter) return true;
    return this.#policy.draw && !this.#policy.blocked.includes(identity);
  }

  #mayShowCursor(identity: string): boolean {
    if (identity === this.presenter) return true;
    return this.#policy.cursor && !this.#policy.blocked.includes(identity);
  }

  #pruneBlocked(): void {
    if (!this.#policy.blocked.length) return;
    for (const identity of this.#policy.blocked) {
      if (identity === this.presenter) continue;
      this.#dropAuthor(identity, false);
      this.#cursors.delete(identity);
    }
  }

  #dropAuthor(author: string, touch = true): void {
    const before = this.#strokes.length;
    this.#strokes = this.#strokes.filter((s) => s.author !== author);
    if (author === this.self) this.#active = undefined;
    if (touch && this.#strokes.length !== before) this.#touch();
  }

  #find(author: string, id: number): Stroke | undefined {
    for (let i = this.#strokes.length - 1; i >= 0; i--) {
      const stroke = this.#strokes[i];
      if (stroke.author === author && stroke.id === id) return stroke;
    }
    return undefined;
  }

  #append(
    author: string,
    id: number,
    chunk: number,
    points: number[],
    style: { color?: number; width?: number; ttl?: number; shape?: number },
  ): void {
    let stroke = this.#find(author, id);

    if (!stroke) {
      // A chunk for a stroke we have never seen: only the opening one may
      // start it. Anything else is a leftover from before we joined.
      if (chunk !== 0) return;
      stroke = {
        author,
        id,
        shape: style.shape ?? SHAPE_FREE,
        points: [],
        color:
          style.color !== undefined
            ? INK_PALETTE[style.color % INK_PALETTE.length]
            : this.colorOf(author),
        width: style.width ?? DEFAULT_WIDTH,
        ttl: style.ttl ?? DEFAULT_TTL_MS,
        done: false,
      };
      this.#strokes.push(stroke);
    }

    if (chunk === 0) {
      // Нулевой кусок — определяющий: у фигуры он приходит на каждое движение,
      // у линии однажды. В обоих случаях заменить правильнее, чем дописать —
      // повтор после переподключения не задвоит штрих.
      stroke.points = points.slice();
    } else {
      // Chunks overlap by one point; the duplicate would show up as a stall in
      // pressure-free ink and wastes budget.
      const skip = stroke.points.length ? 2 : 0;
      for (let i = skip; i < points.length; i++) stroke.points.push(points[i]);
    }

    this.#enforceBudget(author);
    this.#touch();
  }

  /**
   * One participant must not be able to fill the tab with ink. Oldest strokes
   * of that author go first — the newest is the one being looked at.
   */
  #enforceBudget(author: string): void {
    let total = 0;
    for (const stroke of this.#strokes) {
      if (stroke.author === author) total += stroke.points.length / 2;
    }
    if (total <= MAX_LIVE_POINTS) return;

    for (let i = 0; i < this.#strokes.length && total > MAX_LIVE_POINTS; ) {
      const stroke = this.#strokes[i];
      if (stroke.author !== author || (stroke.author === this.self && !stroke.done)) {
        i++;
        continue;
      }
      total -= stroke.points.length / 2;
      this.#strokes.splice(i, 1);
    }
  }

  #touch(): void {
    this.#revision++;
    this.#onChange?.();
  }
}
